// DTOs for cross-context calls into accounting [07 §3 journals + G7 locks, §10 posting].
import { z } from 'zod';

import { businessDateSchema, idSchema, uuidV7Schema } from '../kernel/index.ts';
import { currencyCodeSchema, moneySchema, fxRateDecimalSchema } from '../units/index.ts';
import { LOCK_MODULE, SAGE_BATCH_TYPE } from '../enums/index.ts';
import { gateCheckSchema } from './common.ts';

/** Auto journal from a posting rule; lines use account-mapping keys, never accounts [07 §10, D22]. */
export const autoJournalInputSchema = z.strictObject({
  sourceEventId: uuidV7Schema,
  ruleCode: z.string().regex(/^P\d{2}$/),
  journalDate: businessDateSchema,
  currency: currencyCodeSchema,
  fxRate: fxRateDecimalSchema.nullable(),
  bridgeType: SAGE_BATCH_TYPE.schema.nullable(),
  lines: z
    .array(
      z.strictObject({
        mappingKey: z.string().regex(/^[A-Z][A-Z0-9_]*(\[[^\]]+\])?$/),
        debit: moneySchema.nullable(),
        credit: moneySchema.nullable(),
        memo: z.string().nullable(),
      }),
    )
    .min(2),
});

export const journalRefDtoSchema = z.strictObject({
  journalId: idSchema,
  number: z.string().min(1),
  periodId: idSchema,
  posted: z.boolean(),
});

export const periodStatusQuerySchema = z.strictObject({
  module: LOCK_MODULE.schema,
  date: businessDateSchema,
});

/** G7 closed period: not overridable — `periods.reopen` [02 §10]. */
export const periodStatusDtoSchema = z.strictObject({
  periodId: idSchema,
  fiscalYear: z.int(),
  period: z.int().min(0).max(13),
  open: z.boolean(),
  gate: gateCheckSchema,
});

/** posting_log status per (event id, rule code) [07 BR-PST-002]. */
export const postingStatusValues = ['posted', 'failed', 'skipped'] as const;

export const postingOutcomeDtoSchema = z.strictObject({
  eventId: uuidV7Schema,
  results: z.array(
    z.strictObject({
      ruleCode: z.string().regex(/^P\d{2}$/),
      status: z.enum(postingStatusValues),
      journalId: idSchema.nullable(),
      error: z.string().nullable(),
    }),
  ),
});

export type AutoJournalInput = z.output<typeof autoJournalInputSchema>;
export type JournalRefDto = z.output<typeof journalRefDtoSchema>;
export type PeriodStatusQuery = z.output<typeof periodStatusQuerySchema>;
export type PeriodStatusDto = z.output<typeof periodStatusDtoSchema>;
export type PostingOutcomeDto = z.output<typeof postingOutcomeDtoSchema>;
